import { useContext, useState } from 'react';
import { orderBy } from 'lodash';
import { useTranslation } from 'react-i18next';
import { Button } from '@mui/material';
import { DATE_FORMATS } from '@callmart/types';
import { ReactComponent as FileExport } from '../../assets/file-export.svg';
import { getStrategicVolumeReports } from '../../services/strategicVolumeReport';
import { StrategicVolumeReportRelease } from '../../enums/StrategicVolumeReportRelease';
import ExportToExcelButton from '../Buttons/ExportToExcelButton';
import UserContext from '../../store/User/UserContext';
import StrategicVolumeExportDialog from './StrategicVolumeExportDialog';
import dayjs from '@utils/dayjs';

const StrategicVolumeExportButton = ({ locations }) => {
    const { t } = useTranslation('bidding');
    const loggedInUser = useContext(UserContext);

    const exportColumnNames = [
        t('field.city'),
        t('field.worktype'),
        t('field.channel'),
        t('field.task'),
        t('field.forecaster'),
        t('field.contactType'),
        t('field.comment'),
        t('field.unit'),
        t('field.date'),
        t('field.strategicVolumeValue'),
    ];
    // WFM (folded into isTEFUser) and WTM share the manager export view: 12 months,
    // all locations, all release types, via the date-range dialog. OSP gets the
    // released-only default scoped to its locations.
    const managerView = Boolean(loggedInUser?.isTEFUser || loggedInUser?.isWTMUser);
    const numOfNextMonths = managerView ? 12 : 6;

    const [openExportDialog, setOpenExportDialog] = useState(false);
    const [startDate, setStartDate] = useState(!managerView ? dayjs().format(DATE_FORMATS.ISO_DATE) : '');
    const [endDate, setEndDate] = useState(
        !managerView ? dayjs().add(numOfNextMonths, 'months').format(DATE_FORMATS.ISO_DATE) : ''
    );

    const handleExportDialogClose = () => {
        setOpenExportDialog(false);
    };

    const getDataFromDb = async () => {
        // Strategic volume reports use UTC (no timezone shift)
        const dateFrom = dayjs.utc(startDate).startOf('month').toISOString();
        const dateTo = dayjs.utc(endDate).endOf('month').toISOString();

        const location = managerView ? null : locations.map((location) => location.id).join(',');
        const releaseType = managerView ? null : StrategicVolumeReportRelease.RELEASED;

        return await getStrategicVolumeReports({
            location,
            dateFrom,
            dateTo,
            user: loggedInUser.id,
            releaseType,
        });
    };

    const getExportData = (data) => {
        const exportData = [];
        data.forEach((strategicVolume) => {
            const location = locations.find(
                (location) => location.id === strategicVolume.workingUnitLocation.worktypeLocation.locationId
            );

            const reports = orderBy(strategicVolume.strategicVolumeReports, ['date'], ['asc']);
            reports.forEach((strategicVolumeReport) => {
                exportData.push({
                    strategicVolumeId: strategicVolume.id,
                    forecasterNames: strategicVolume.forecasterNames.join(','),
                    capacityTypeUnit: strategicVolume.capacityType.unit,
                    reportValue: strategicVolumeReport?.value ?? null,
                    code: location?.code ?? null,
                    city: location?.city ?? null,
                    worktype: strategicVolume?.workingUnitLocation?.workingUnit?.worktype?.worktype,
                    channel: strategicVolume?.workingUnitLocation?.workingUnit?.channel?.name,
                    task: strategicVolume?.workingUnitLocation?.workingUnit?.task?.name,
                    date: strategicVolumeReport.date,
                    comment: strategicVolume.comment,
                    contact: strategicVolume.contact,
                });
            });
        });

        return exportData;
    };

    const createExcelRowForExport = (exportDataRow) => {
        return [
            exportDataRow.code + '-' + exportDataRow.city,
            exportDataRow.worktype,
            exportDataRow.channel,
            exportDataRow.task,
            exportDataRow.forecasterNames,
            exportDataRow.contact,
            exportDataRow.comment,
            exportDataRow.capacityTypeUnit,
            dayjs(exportDataRow.date).startOf('month').format(DATE_FORMATS.DATE),
            exportDataRow.reportValue,
        ];
    };

    const getExportFileName = () => {
        const start = dayjs(startDate).format(DATE_FORMATS.MONTH_YEAR);
        const end = dayjs(endDate).format(DATE_FORMATS.MONTH_YEAR);

        return `${t('export.fileName')}_${start}_${end}.xlsx`;
    };

    return managerView ? (
        <>
            <Button onClick={() => setOpenExportDialog(true)} variant="contained">
                <FileExport />
            </Button>
            {openExportDialog && (
                <StrategicVolumeExportDialog
                    open={openExportDialog}
                    onClose={handleExportDialogClose}
                    locations={locations}
                    createExcelRowForExport={createExcelRowForExport}
                    getExportData={getExportData}
                    getDataFromDb={getDataFromDb}
                    startDate={startDate}
                    onStartDateChange={setStartDate}
                    endDate={endDate}
                    onEndDateChange={setEndDate}
                    exportColumnNames={exportColumnNames}
                ></StrategicVolumeExportDialog>
            )}
        </>
    ) : (
        <ExportToExcelButton
            columnNames={exportColumnNames}
            sheetName="Report"
            getFileName={getExportFileName}
            wbTitle=""
            wbSubject="Export Report"
            createRowFromExportData={createExcelRowForExport}
            getDataFromDb={getDataFromDb}
            getExportData={getExportData}
        />
    );
};

export default StrategicVolumeExportButton;
